import React, { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button, Label, Select, TopProgress } from "../../../../../../components";
import { Typography } from "../../../../../../components";
import { useUi } from "../../../../../../context/ui.context";
import { kubernetesService, clusterSettingsService } from "../../../../../../api";
import { extractErrorMessage } from "../../../../../../utils/error-extractor";
import { Addon } from "../../../../../settings/clusters/types";
import MetricsServerIc from "./icons/metrics-server.svg";
import CloudNativeIc from "./icons/cloudnative-pg.svg";
import PrometheusIc from "./icons/prometheus.svg";
import RedisIc from "./icons/redis.svg";
import NginxIc from "./icons/ingress-nginx.svg";
import DefaultAddonIc from "./icons/default-addon.svg";

const addonIcons: Record<string, string> = {
  "metrics-server": MetricsServerIc,
  "cloudnative-pg": CloudNativeIc,
  "prometheus": PrometheusIc,
  "redis": RedisIc,
  "ingress-nginx": NginxIc,
};

const getAddonIcon = (name: string): string =>
  addonIcons[name] || DefaultAddonIc;

const Container = styled.div`
  padding-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 20px;
`;

const AddonsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`;

const ItemAddon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border-radius: 8px;
  width: 353px;
  flex: 1 auto;
  border: ${({ theme }) => `1px solid ${theme.grey[350]}`};
  background-color: ${({ theme }) => theme.palette[10]};
`;

interface AddonsProps {
  clusterId: string;
}

interface AddonWithReleases extends Addon {
  release?: { label: string; value: string; releaseId: number };
  releasesOptions: { label: string; value: string; releaseId: number }[];
}

const Addons: FC<AddonsProps> = ({ clusterId }) => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  const [addons, setAddons] = useState<AddonWithReleases[]>([]);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    clusterSettingsService
      .getAddons({ page: 1, page_size: 100 })
      .then((res) => {
        const list = (res.data?.objects || []) as Addon[];
        setAddons(
          list.map((addon) => ({
            ...addon,
            release: addon.releases?.[0]
              ? {
                  label: addon.releases[0].version,
                  value: addon.releases[0].version,
                  releaseId: addon.releases[0].id,
                }
              : undefined,
            releasesOptions: (addon.releases || []).map((r) => ({
              label: r.version,
              value: r.version,
              releaseId: r.id,
            })),
          }))
        );
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("ERROR_OCCURRED");
        viewAlert({ severity: "error", message });
      })
      .finally(() => setPageLoading(false));
  }, [t, viewAlert]);

  if (pageLoading) return <TopProgress loading={pageLoading} />;

  return (
    <Container>
      <AddonsWrapper>
        {addons.map((addon) => (
          <AddonItem key={addon.id} addon={addon} clusterId={clusterId} />
        ))}
        {addons.length === 0 && <div>{t("NO_ADDON_RELEASES")}</div>}
      </AddonsWrapper>
    </Container>
  );
};

export default Addons;

interface AddonItemProps {
  addon: AddonWithReleases;
  clusterId: string;
}

const AddonItem: FC<AddonItemProps> = ({ addon, clusterId }) => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  const [version, setVersion] = useState<string>(
    addon.releasesOptions[0]?.value || ""
  );
  const [loading, setLoading] = useState(false);
  const [loadingComplete, setLoadingComplete] = useState(false);

  const installVersion = useCallback(() => {
    setLoading(true);
    const releaseId = addon.releasesOptions.find(
      (r) => r.value === version
    )?.releaseId;
    kubernetesService
      .installAddon(clusterId, {
        release_ids: releaseId ? [releaseId] : [],
      })
      .then(() => {
        setLoadingComplete(true);
        setTimeout(() => {
          setLoading(false);
          setLoadingComplete(false);
        }, 1500);
        viewAlert({
          severity: "information",
          message: t("ADDONS_INSTALLED"),
        });
      })
      .catch((error) => {
        setLoading(false);
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("ERROR_OCCURRED");
        viewAlert({ severity: "error", message });
      });
  }, [clusterId, version, addon, t, viewAlert]);

  return (
    <ItemAddon>
      <div style={{ display: "flex", flexDirection: "row", width: "100%", alignItems: "center" }}>
        <img
          style={{ marginRight: "8px", width: "20px", height: "20px" }}
          src={getAddonIcon(addon.name)}
          alt={addon.name}
        />
        <Label
          margin="10px"
          toolTip={!!addon.description}
          tooltipText={addon.description}
          title={addon.name}
        />
      </div>
      <div
        style={{ display: "flex", flexDirection: "row", gap: "6px", width: "100%", alignItems: "center" }}
      >
        <Typography
          customStyles={{ marginTop: "2px" }}
          variant="small"
        >
          {t("VERSION")}
        </Typography>
        <Select
          toolTip={false}
          required
          isSmall
          data={addon.releasesOptions}
          selectedValue={version}
          onChange={(e: string) => setVersion(e)}
          placeholder={
            addon.releasesOptions.length === 0
              ? t("NO_DATA")
              : t("CHOOSE")
          }
          title={t("VERSION")}
          customStyles={{ minWidth: "61px" }}
        />
      </div>
      <Button
        variant="secondary"
        size="display"
        onClick={installVersion}
        title={t("INSTALL")}
        customStyles={{
          display: "flex",
          width: "100%",
          justifyContent: "center",
          boxSizing: "border-box",
        }}
        loading={loading}
        loadingComplete={loadingComplete}
      />
    </ItemAddon>
  );
};
